import React, { useMemo, useState } from 'react';
import { BodyText, PageSection, Subtitle } from 'common/styles/page';
import { StyledButton } from 'common/components/button/StyledButton';
import LoadingSpinner from 'common/components/LoadingSpinner';
import PositionedModal from 'common/components/PositionedModal';
import { useToasts } from 'common/toast';
import {
  useAdminAggregateHealthQuery,
  useAdminAggregateReconcileMutation,
  AggregateMatrixRow,
} from '../api/aggregateHealthApi';
import { DropdownProps, Modal, Select } from 'semantic-ui-react';

const AGGREGATE_DISPLAY_NAMES: Record<string, string> = {
  ALL_AGGREGATES: 'All Aggregates',
  PILLAR_SCORES: 'Organization Pillar Metrics',
  AI_SUMMARY_TEAM: 'AI Summary Team',
  SENTIMENT_SCORES: 'Sentiment Scores',
  GREATEST_STRENGTHS: 'AI Greatest Strengths',
  GREATEST_DEFICITS: 'AI Greatest Opportunities',
  MEMBER_PILLARS: 'Member Pillars',
  DEPARTMENT_PILLARS: 'Department Pillars',
  CATEGORY_PILLARS: 'Category Pillars',
  OUTCOME_SCORES: 'Outcome Scores',
  CATEGORY_OVERALL: 'Category Overall',
  AI_DATA_PAYLOAD: 'AI Data Payload',
  MEMBER_QUESTION_SCORES: 'Member Question Scores',
  OPTION_QUESTION_SCORES: 'Option Question Scores',
  CATEGORY_QUESTION_SCORES: 'Category Question Scores',
  MEMBER_FEEDBACK: 'Member Feedback',
  AI_SUMMARY_ORGANIZATION: 'AI Summary Organization',
  AI_EMBEDDINGS: 'AI Embeddings',
  OUTCOME_PREDICTIONS: 'Outcome Predictions',
  CARTESIAN_AVERAGES: 'Multi-factor Cartesian',
  LOOKUP_PROPERTIES: 'Lookup Properties',
  SURVEY_COMPLETION_STATISTICS: 'Survey Completion Statistics',
  OPTION_OUTCOME_SCORES: 'Option Outcome Scores',
  CATEGORY_OUTCOME_SCORES: 'Category Outcome Scores',
  THEMES: 'AI Themes',
  KEYWORDS: 'AI Wordcloud Keywords',
  TEAM_SUMMARY: 'AI Team Summary',
  SENTIMENT_DISTRIBUTION: 'AI Sentiment Distribution',
  TEAM_OUTCOME_METRICS: 'Team Outcome Metrics',
  TEAM_PILLAR_METRICS: 'Team Pillar Metrics',
  EMERGENT_THEMES: 'AI Emergent Themes',
};

function humanizeAggregate(name: string): string {
  return name
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function displayAggregateName(name: string): string {
  return AGGREGATE_DISPLAY_NAMES[name] ?? humanizeAggregate(name);
}

function cellKey(periodId: number, aggregateType: number): string {
  return `${periodId}:${aggregateType}`;
}

const HEADER_HEIGHT = 150;
const LABEL_WIDTH = 260;

const AggregateHealth: React.FC = () => {
  const { data, isLoading, isError } = useAdminAggregateHealthQuery();
  const [runReconcile, { isLoading: isRunning }] = useAdminAggregateReconcileMutation();
  // Start with no selection by default; users must explicitly choose aggregates to recompute.
  const [override, setOverride] = useState<Set<string> | null>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectMode, setSelectMode] = useState<'none' | 'all' | 'never_computed' | 'recommended'>('none');
  const { addToast } = useToasts();

  const columns = useMemo(
    () =>
      (data?.columns ?? [])
        .slice()
        .sort((a, b) => displayAggregateName(a.name).localeCompare(displayAggregateName(b.name))),
    [data],
  );
  const rows = useMemo(
    () => (data?.rows ?? []),
    [data],
  );

  const groupedRows = useMemo(() => {
    const groups = new Map<string, { organization_id: number | null; organization_name: string; rows: typeof rows }>();
    rows.forEach(row => {
      const orgKey = `${row.organization_id ?? 'none'}:${row.organization_name ?? ''}`;
      const group = groups.get(orgKey) ?? {
        organization_id: row.organization_id ?? null,
        organization_name: row.organization_name || 'Unknown organization',
        rows: [],
      };
      group.rows.push(row);
      groups.set(orgKey, group);
    });
    return Array.from(groups.values());
  }, [rows]);

  const recommendedKeys = useMemo(() => {
    const keys = new Set<string>();
    rows.forEach(row =>
      row.recommended.forEach(type => keys.add(cellKey(row.assessment_period_id, type))),
    );
    return keys;
  }, [rows]);

  const checked = override ?? recommendedKeys;

  function setCells(keys: string[], value: boolean) {
    setOverride(prev => {
      const next = new Set(prev ?? recommendedKeys);
      keys.forEach(key => (value ? next.add(key) : next.delete(key)));
      return next;
    });
  }

  function toggleCell(periodId: number, aggregateType: number) {
    const key = cellKey(periodId, aggregateType);
    setCells([key], !checked.has(key));
  }

  function rowKeys(row: AggregateMatrixRow) {
    return columns.map(column => cellKey(row.assessment_period_id, column.aggregate_type));
  }

  function columnKeys(aggregateType: number) {
    return rows.map(row => cellKey(row.assessment_period_id, aggregateType));
  }

  const allKeys = useMemo(
    () => rows.flatMap(row => rowKeys(row)),
    [rows, columns],
  );

  const neverComputedKeys = useMemo(() => {
    const keys: string[] = [];
    rows.forEach(row => {
      columns.forEach(column => {
        const exists = row.present.includes(column.aggregate_type);
        if (!exists) {
          keys.push(cellKey(row.assessment_period_id, column.aggregate_type));
        }
      });
    });
    return keys;
  }, [rows, columns]);

  function updateSelectionMode(mode: 'none' | 'all' | 'never_computed' | 'recommended') {
    setSelectMode(mode);
    switch (mode) {
      case 'none':
        setOverride(new Set());
        break;
      case 'all':
        setOverride(new Set(allKeys));
        break;
      case 'never_computed':
        setOverride(new Set(neverComputedKeys));
        break;
      case 'recommended':
        setOverride(null);
        break;
    }
  }

  const selection = useMemo(
    () =>
      Array.from(checked).map(key => {
        const [periodId, aggregateType] = key.split(':');
        return {
          assessment_period_id: Number(periodId),
          aggregate_type: Number(aggregateType),
        };
      }),
    [checked],
  );

  const isDirty =
    override !== null &&
    (checked.size !== recommendedKeys.size ||
      Array.from(checked).some(key => !recommendedKeys.has(key)));

  async function handleSubmit() {
    try {
      const result = await runReconcile({ selection }).unwrap();
      addToast(result.message ?? `Started ${selection.length} recompute(s)`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Recompute failed:', error);
      addToast('Failed to start recomputes', { appearance: 'error', autoDismiss: true });
    }
  }

  const allChecked = allKeys.length > 0 && checked.size === allKeys.length;

  return (
    <PageSection padbottom data-testid="admin-aggregate-health">
      <PageSection padbottom="10px">
        <Subtitle>Aggregates</Subtitle>
        <BodyText>
          Checked aggregates are recomputed on submit. 
        </BodyText>
      </PageSection>

      {isLoading && <LoadingSpinner />}
      {!isLoading && isError && <BodyText>Unable to load aggregates.</BodyText>}

      {!isLoading && !isError && (
        <>
          {/* Select-all lives in the grid's corner checkbox, so it isn't repeated here. */}
          <PageSection
            gap="10px"
            padbottom="10px"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Select
              placeholder="Selection"
              value={selectMode}
              options={[
                { key: 'none', value: 'none', text: 'None' },
                { key: 'all', value: 'all', text: 'All Aggregates' },
                { key: 'never_computed', value: 'never_computed', text: 'Never Computed' },
                { key: 'recommended', value: 'recommended', text: 'Outdated Version' },
              ]}
              onChange={(_e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) =>
                updateSelectionMode(data.value as 'none' | 'all' | 'never_computed' | 'recommended')
              }
            />
            <StyledButton
              data-testid="aggregate-health-submit"
              onClick={() => setConfirmOpen(true)}
              disabled={isRunning || checked.size === 0}
              label={isRunning ? 'Starting...' : `Recompute`}
              color="red"
            />
          </PageSection>
          <PositionedModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            size="small"
            closeIcon
          >
            <Modal.Header>Confirm Recompute</Modal.Header>
            <Modal.Content>
              <p>
                Are you sure you want to recompute {checked.size} aggregate{checked.size === 1 ? '' : 's'}?
              </p>
            </Modal.Content>
            <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <StyledButton
                onClick={() => setConfirmOpen(false)}
                label="Cancel"
                color="gray"
              />
              <StyledButton
                onClick={async () => {
                  setConfirmOpen(false);
                  await handleSubmit();
                }}
                label={isRunning ? 'Starting...' : 'Confirm'}
                color="red"
                disabled={isRunning}
              />
            </Modal.Actions>
          </PositionedModal>

          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <table
              data-testid="aggregate-health-matrix"
              style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      height: HEADER_HEIGHT,
                      width: LABEL_WIDTH,
                      minWidth: LABEL_WIDTH,
                      textAlign: 'left',
                      verticalAlign: 'bottom',
                      padding: '0 8px 8px 0',
                      position: 'sticky',
                      left: 0,
                      background: '#fff',
                      zIndex: 2,
                    }}
                  >
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        data-testid="aggregate-health-toggle-all"
                        type="checkbox"
                        checked={allChecked}
                        onChange={() => setCells(allKeys, !allChecked)}
                      />{' '}
                      Organization
                    </label>
                  </th>
                  <th
                    style={{
                      height: HEADER_HEIGHT,
                      width: 120,
                      minWidth: 120,
                      textAlign: 'left',
                      verticalAlign: 'bottom',
                      padding: '0 8px 8px 0',
                      background: '#fff',
                    }}
                  >
                    Period ID
                  </th>
                  {columns.map(column => {
                    const keys = columnKeys(column.aggregate_type);
                    const columnChecked = keys.length > 0 && keys.every(key => checked.has(key));
                    return (
                      <th
                        key={column.aggregate_type}
                        style={{
                          height: HEADER_HEIGHT,
                          verticalAlign: 'bottom',
                          padding: '0 0 8px 0',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <div
                          style={{
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                            margin: '0 auto 6px',
                            fontWeight: 500,
                          }}
                        >
                          {displayAggregateName(column.name)} {`(${column.aggregate_type})`}
                        </div>
                        <input
                          data-testid={`aggregate-health-column-${column.aggregate_type}`}
                          type="checkbox"
                          title={`Select all ${humanizeAggregate(column.name)}`}
                          checked={columnChecked}
                          onChange={() => setCells(keys, !columnChecked)}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {groupedRows.map(group => (
                  <React.Fragment key={group.organization_id ?? group.organization_name}>
                    <tr>
                      <td
                        style={{
                          width: LABEL_WIDTH,
                          minWidth: LABEL_WIDTH,
                          padding: '10px 8px 10px 0',
                          borderTop: '1px solid #eee',
                          position: 'sticky',
                          left: 0,
                          background: '#f8fafc',
                          zIndex: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 700,
                        }}
                        colSpan={columns.length + 2}
                      >
                        {group.organization_name}
                      </td>
                    </tr>
                    {group.rows.map(row => {
                      const keys = rowKeys(row);
                      const rowChecked = keys.length > 0 && keys.every(key => checked.has(key));
                      return (
                        <tr key={row.assessment_period_id}>
                          <td
                            style={{
                              width: LABEL_WIDTH,
                              minWidth: LABEL_WIDTH,
                              padding: '4px 8px 4px 0',
                              borderTop: '1px solid #eee',
                              position: 'sticky',
                              left: 0,
                              background: '#fff',
                              zIndex: 1,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={row.assessment_period_name}
                          >
                            <label style={{ cursor: 'pointer' }}>
                              <input
                                data-testid={`aggregate-health-row-${row.assessment_period_id}`}
                                type="checkbox"
                                checked={rowChecked}
                                onChange={() => setCells(keys, !rowChecked)}
                              />{' '}
                              {row.assessment_period_name}
                            </label>
                          </td>
                          <td
                            style={{
                              width: 120,
                              minWidth: 120,
                              padding: '4px 8px 4px 0',
                              borderTop: '1px solid #eee',
                              background: '#fff',
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={`Period ID: ${row.assessment_period_id}`}
                          >
                            {row.assessment_period_id}
                          </td>
                          {columns.map(column => {
                            const key = cellKey(row.assessment_period_id, column.aggregate_type);
                            const isRecommended = recommendedKeys.has(key);
                            const exists = row.present.includes(column.aggregate_type);
                            return (
                              <td
                                key={column.aggregate_type}
                                style={{
                                  textAlign: 'center',
                                  padding: '4px 6px',
                                  borderTop: '1px solid #eee',
                                  background: isRecommended ? '#fdecea' : !exists ? '#fae897' : undefined,
                                }}
                                title={
                                  isRecommended
                                    ? 'Recommended — calculation version is out of date'
                                    : exists
                                      ? 'Up to date'
                                      : 'Never computed'
                                }
                              >
                                <input
                                  data-testid={`aggregate-health-cell-${key}`}
                                  type="checkbox"
                                  checked={checked.has(key)}
                                  onChange={() =>
                                    toggleCell(row.assessment_period_id, column.aggregate_type)
                                  }
                                  style={{ opacity: exists || isRecommended ? 1 : 0.45 }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageSection>
  );
};

export default AggregateHealth;
